import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { ASPECTS, PLANETS, type AspectId, type PlanetId } from '@yadviga/shared'
import { api, type CurrentTransitsPayload } from '../../api/client'
import { useSession } from '../../store/user.store'
import { Button } from '../../components/ui/Button'
import { PageHeader } from '../../components/ui/PageHeader'
import { LoadingDots } from '../../components/ui/LoadingDots'
import { ErrorState } from '../../components/ui/ErrorState'
import { MarkdownText } from '../../components/ui/Markdown'
import { BirthModal } from './birth-modal'

const ASPECT_TONE: Record<AspectId, string> = {
  conjunction: 'bg-sage-100 text-sage-700',
  trine: 'bg-emerald-100 text-emerald-700',
  sextile: 'bg-emerald-50 text-emerald-700',
  square: 'bg-amber-100 text-amber-700',
  opposition: 'bg-amber-100 text-amber-700',
}

type TransitCardData = CurrentTransitsPayload['transits'][number]

export function AstroTransitsRoute() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const user = useSession((s) => s.user)
  const lang = (user?.language ?? 'ru') as 'ru' | 'en'
  const [birthOpen, setBirthOpen] = useState(false)

  const query = useQuery({
    queryKey: ['astro', 'transits'],
    queryFn: () => api.currentTransits(),
    // Planet positions move slowly + the AI summary is cached server-side
    // for 25h. Half an hour of FE freshness is plenty.
    staleTime: 1000 * 60 * 30,
  })

  const data = query.data

  return (
    <div className="flex flex-col gap-5 pb-6">
      <PageHeader
        title={t('astro.transits_title')}
        subtitle={t('astro.transits_intro')}
        backTo="/astro"
      />

      {query.isLoading && <LoadingDots compact />}
      {query.isError && <ErrorState onRetry={() => query.refetch()} />}

      {/* No birth data → prompt the user to fill it in. */}
      {data === null && (
        <section className="flex flex-col gap-3 rounded-3xl bg-white/70 p-5 text-center ring-1 ring-sage-200/60">
          <p className="text-sm text-sage-700">{t('astro.transits_need_birth')}</p>
          <Button onClick={() => setBirthOpen(true)}>{t('profile.fill_birth')}</Button>
        </section>
      )}

      {data && data.transits.length === 0 && (
        <p className="rounded-2xl bg-white/60 p-5 text-center text-sm text-sage-700 ring-1 ring-sage-200/60">
          {t('astro.transits_none')}
        </p>
      )}

      {data && data.transits.length > 0 && (
        <>
          {data.summary && (
            <section className="rounded-3xl bg-gradient-to-br from-[#fbeadd] to-[#f5d9c0] p-5 ring-1 ring-[#e6c4a4]/60">
              <p className="text-xs uppercase tracking-widest text-copper">
                {t('astro.transits_summary_eyebrow')}
              </p>
              <div className="mt-2 text-sage-800 leading-relaxed">
                <MarkdownText>{data.summary}</MarkdownText>
              </div>
            </section>
          )}

          <ul className="flex flex-col gap-3">
            {data.transits.map((t, i) => (
              <TransitCard key={`${t.transitPlanet}-${t.aspect}-${t.natalPlanet}-${i}`} transit={t} lang={lang} />
            ))}
          </ul>

          {data.natalTier === 'teaser' && <FullNatalUpsell />}
        </>
      )}

      <BirthModal
        open={birthOpen}
        onClose={() => setBirthOpen(false)}
        onSaved={() => qc.invalidateQueries({ queryKey: ['astro', 'transits'] })}
      />
    </div>
  )
}

function TransitCard({ transit, lang }: { transit: TransitCardData; lang: 'ru' | 'en' }) {
  const { t } = useTranslation()
  const tp = planetLabel(transit.transitPlanet as PlanetId, lang)
  const np = planetLabel(transit.natalPlanet as PlanetId, lang)
  const aspectId = transit.aspect as AspectId
  const asp = aspectLabel(aspectId, lang)
  return (
    <li className="flex flex-col gap-2 rounded-2xl bg-white/70 p-4 ring-1 ring-sage-200/60">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-serif text-base text-sage-800">
          <span>{tp.symbol}</span> <span className="text-sage-600">{tp.name}</span>{' '}
          <span className="text-sage-500">→</span>{' '}
          <span>{np.symbol}</span> <span className="text-sage-600">{np.name}</span>
        </h3>
        <span className={clsx('rounded-full px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wider', ASPECT_TONE[aspectId])}>
          {asp}
        </span>
      </div>
      {transit.text && (
        <p className="text-sm text-sage-700 leading-relaxed">{transit.text}</p>
      )}
      <p className="text-xs text-sage-500">
        {t('astro.transits_orb', { orb: transit.orb.toFixed(1) })}
        {transit.exact && <span className="ml-2 text-copper">· {t('astro.transits_exact')}</span>}
      </p>
    </li>
  )
}

/** Shown when transits are computed against a teaser natal (sun/moon only).
 *  Lets the user know unlocking the full chart enriches this very page. */
function FullNatalUpsell() {
  const { t } = useTranslation()
  return (
    <Link
      to="/astro/natal"
      className="flex items-center gap-3 rounded-3xl bg-gradient-to-br from-[#fdf3e3] to-[#fbeadd] p-4 ring-1 ring-[#e6c4a4]/60 transition active:scale-[0.99]"
    >
      <img
        src="/icons/natal.png"
        alt=""
        aria-hidden="true"
        className="h-10 w-10 shrink-0 select-none"
        draggable={false}
      />
      <div className="min-w-0 flex-1">
        <h3 className="font-serif text-base text-sage-800">{t('astro.transits_upsell_title')}</h3>
        <p className="text-xs text-sage-700">{t('astro.transits_upsell_desc')}</p>
      </div>
    </Link>
  )
}

function planetLabel(id: PlanetId, lang: 'ru' | 'en'): { name: string; symbol: string } {
  const p = PLANETS.find((x) => x.id === id)
  if (!p) return { name: id, symbol: '' }
  return { name: lang === 'en' ? p.nameEn : p.nameRu, symbol: p.symbol }
}

function aspectLabel(id: AspectId, lang: 'ru' | 'en'): string {
  const a = ASPECTS.find((x) => x.id === id)
  if (!a) return id
  return lang === 'en' ? a.nameEn : a.nameRu
}
